import React, { memo } from "react";
import { Box, Container, Stack, Typography } from "@mui/material";
import PropertySlider from "../../../../Components/PropertySlider/PropertySlider";
import { RightPaddingWrapper } from "../../../../Components/RightPaddingWrapper/RightPaddingWrapper";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setToggleAuthModal } from "../../../../Redux/authReducer";
import { TRADE_LABELS } from "../../../../Services/contractApi";

// The "Need Help?" section is driven by the bidding trade types, so each card
// maps 1:1 to a real trade and links straight into the vendor-bidding flow.
// List-driven on purpose: adding more later (furniture, cleaning, ...) is a
// one-line change once those trades + vendors exist.
const TRADES: { key: string; img: string }[] = [
  { key: "plumbing", img: "/services/plumbing.jpg" },
  { key: "electrical", img: "/services/electrical.jpg" },
  { key: "roofing", img: "/services/roofing.jpg" },
  { key: "hvac", img: "/services/hvac.jpg" },
  { key: "painting", img: "/services/painting.jpg" },
  { key: "flooring", img: "/services/flooring.jpg" },
  { key: "windows_doors", img: "/services/windows_doors.jpg" },
  { key: "general_contractor", img: "/services/general_contractor.jpg" },
];

const LocalServices = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((s: any) => s?.auth || {});

  // "Request a service" -> the bidding flow. Bidding is property-scoped and
  // owner-only, so route by auth + how many homes the user has claimed.
  const handleTradeClick = (trade: string) => {
    if (!isAuthenticated) {
      dispatch(setToggleAuthModal(true)); // open login / sign-up
      return;
    }
    const owned = user?.propertyCollections || [];
    if (owned.length === 0) {
      navigate("/my-account", { state: { serviceCta: "claim", serviceTrade: trade } });
      return;
    }
    if (owned.length === 1) {
      const p = owned[0];
      const pid = p?.propertyId?.propertyId || p?.propertyId?._id;
      if (pid) {
        navigate(`/${pid}/property-id/${pid}?tab=find-a-pro&trade=${trade}`);
        return;
      }
    }
    navigate("/my-account", { state: { serviceCta: "select", serviceTrade: trade } });
  };

  return (
    <Container maxWidth={"xl"} disableGutters>
      <RightPaddingWrapper>
        <Stack direction={"column"} spacing={"30px"}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Need Help? Plumbers, Roofers, Contractors, and More.
            </Typography>
          </Box>
          <Box>
            <PropertySlider>
              {TRADES.map((t) => {
                const label = TRADE_LABELS[t.key] || t.key;
                return (
                  <Box key={t.key} sx={{ px: { xs: 0, sm: 1 }, py: 1 }}>
                    <Box
                      onClick={() => handleTradeClick(t.key)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") handleTradeClick(t.key);
                      }}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "15px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        border: "1px solid #eee",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                      }}
                    >
                      <Box
                        component="img"
                        src={t.img}
                        alt={label}
                        loading="lazy"
                        sx={{
                          width: "100%",
                          height: { xs: 200, md: 220 },
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      <Box sx={{ p: "18px 20px 22px" }}>
                        <Typography fontWeight={700} fontSize={"18px"}>
                          {label}
                        </Typography>
                        <Typography fontSize={"14px"} color="text.secondary" mt={0.5}>
                          Free help from trusted local pros
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </PropertySlider>
          </Box>
        </Stack>
      </RightPaddingWrapper>
    </Container>
  );
});

export default LocalServices;
